import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api from '../api';

/**
 * OnboardingTour
 * A lightweight spotlight-style guided walkthrough that auto-runs once
 * per user on their first authenticated visit. Highlights the three
 * essentials: Create Clan wizard, Referral link, Wallet deposit.
 *
 * DIY (no external library) to keep the bundle small and the styling
 * on-brand. Steps target DOM nodes by data-testid; if a target isn't
 * mounted we gracefully advance to the next step.
 *
 * Copy lives in i18n under `tours.onboarding.*`. STEPS array holds only
 * ids, selectors, placement, and menu-open flags. The "Fair by design"
 * step has structured content (paragraph + bullet list) — rendered via
 * <Trans> with named slots so translators get self-contained sentences.
 *
 * Persistence: backend `POST /api/auth/tour-complete` sets
 * `user.tour_completed_at`. Manual restart via `POST /api/auth/tour-reset`.
 */

const STEPS = [
  { id: 'welcome',       target: null                                                                                                       },
  { id: 'home',          target: '[data-testid="nav-home"], [data-testid="tab-home"]',                 placement: 'bottom'                    },
  { id: 'walletDeposit', target: '[data-testid="menu-wallet"]',                                        placement: 'left',   openMenu: true    },
  { id: 'createClan',    target: '[data-testid="nav-clan-cta"], [data-testid="tab-clan-cta"]',         placement: 'bottom'                    },
  { id: 'discover',      target: '[data-testid="search-input"]',                                       placement: 'bottom'                    },
  { id: 'buySell',       target: null                                                                                                       },
  { id: 'fairByDesign',  target: null,                                                                 richBody: true                         },
  { id: 'tradeArcade',   target: '[data-testid="nav-trade"], [data-testid="tab-trade"]',               placement: 'bottom'                    },
  { id: 'activeClans',   target: '[data-testid="nav-clans"], [data-testid="tab-clans"]',               placement: 'bottom'                    },
  { id: 'launches',      target: '[data-testid="menu-launches"]',                                      placement: 'bottom', openMenu: true    },
  { id: 'bounties',      target: '[data-testid="nav-bounties"], [data-testid="tab-bounties"]',         placement: 'bottom'                    },
  { id: 'perks',         target: '[data-testid="nav-perks"], [data-testid="tab-perks"]',               placement: 'bottom'                    },
  { id: 'referrals',     target: '[data-testid="menu-referrals"]',                                     placement: 'left',   openMenu: true    },
  { id: 'walletManage',  target: '[data-testid="menu-wallet"]',                                        placement: 'left',   openMenu: true    },
  { id: 'done',          target: null                                                                                                       },
];

const PADDING = 8;

function ensureMenuOpen() {
  const btn = document.querySelector('[data-testid="avatar-btn"]');
  const menu = document.querySelector('[data-testid="avatar-menu"]');
  if (btn && !menu) btn.click();
}

function ensureMenuClosed() {
  const menu = document.querySelector('[data-testid="avatar-menu"]');
  if (menu) {
    const btn = document.querySelector('[data-testid="avatar-btn"]');
    if (btn) btn.click();
  }
}

export default function OnboardingTour() {
  const { user, refreshMe } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [active, setActive] = useState(false);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [ready, setReady] = useState(false);
  const bootedRef = useRef(false);

  // Auto-boot the tour once, on the first authenticated visit that lands
  // on /home. Skips gracefully for admins, suspended users, and anyone
  // who has already dismissed / completed it. Also HOLDS if the fresh
  // signup hasn't yet dismissed the WelcomeModal — the welcome note
  // sets `welcome_shown_at`; tour boots on the next `user` refresh.
  useEffect(() => {
    if (bootedRef.current) return;
    if (!user || user === false) return;
    if (user.tour_completed_at) return;
    if (user.role === 'admin') return;
    if (user.suspended) return;
    // Only auto-boot on /home so the anchors are guaranteed to exist.
    if (location.pathname !== '/home') return;
    // Wait for the WelcomeModal to be dismissed before we start the tour.
    // (The modal itself gates on `!welcome_shown_at`.)
    if (!user.welcome_shown_at) return;
    bootedRef.current = true;
    // Give the shell + nav bar a beat to mount before we measure.
    const t = setTimeout(() => {
      setActive(true);
      setIdx(0);
    }, 900);
    return () => clearTimeout(t);
  }, [user, location.pathname]);

  const step = active ? STEPS[idx] : null;

  // Pick the FIRST element matching `selector` that is actually visible in
  // the layout (non-zero size + not display:none/visibility:hidden). This
  // supports comma-separated selectors like `[nav-home], [tab-home]`
  // where desktop mounts one and mobile mounts the other — we always
  // spotlight the one currently rendered.
  const pickVisible = (selector) => {
    if (!selector) return null;
    const nodes = document.querySelectorAll(selector);
    for (const n of nodes) {
      const r = n.getBoundingClientRect();
      const cs = window.getComputedStyle(n);
      if (r.width > 0 && r.height > 0 && cs.display !== 'none' && cs.visibility !== 'hidden') {
        return n;
      }
    }
    // Fallback: any element (even zero-size) if none were "visible"; the
    // fit-check below will treat it as centered.
    return nodes[0] || null;
  };

  // Measure the target rect (with a small retry loop, since Step 4 & 5
  // depend on the avatar menu having just been programmatically opened).
  useLayoutEffect(() => {
    if (!active || !step) { setRect(null); setReady(false); return; }
    if (step.openMenu) ensureMenuOpen();
    else ensureMenuClosed();
    if (!step.target) { setRect(null); setReady(true); return; }

    let cancelled = false;
    let tries = 0;
    const measure = () => {
      if (cancelled) return;
      const el = pickVisible(step.target);
      if (el) {
        const r = el.getBoundingClientRect();
        // If the picked element has zero size (nothing visible mounted),
        // treat as centered so we don't spotlight the corner.
        if (r.width > 0 && r.height > 0) {
          setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
          setReady(true);
          return;
        }
      }
      if (++tries < 20) {
        setTimeout(measure, 100);
      } else {
        // Target never showed — advance so we don't dead-end the tour.
        setReady(true);
        setRect(null);
      }
    };
    measure();

    const onScroll = () => {
      if (!step.target) return;
      const el = pickVisible(step.target);
      if (el) {
        const r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) {
          setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        }
      }
    };
    window.addEventListener('resize', onScroll);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      cancelled = true;
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [active, idx, step]);

  const finish = useCallback(async () => {
    setActive(false);
    ensureMenuClosed();
    try {
      await api.post('/auth/tour-complete');
      await refreshMe();
    } catch (_) { /* non-fatal */ }
  }, [refreshMe]);

  const next = useCallback(() => {
    if (idx >= STEPS.length - 1) { finish(); return; }
    setIdx((i) => i + 1);
  }, [idx, finish]);

  const back = useCallback(() => {
    setIdx((i) => Math.max(0, i - 1));
  }, []);

  if (!active || !step || !ready) return null;

  const isCentered = !step.target || !rect;

  // Position the tooltip near the target rect (with viewport clamping).
  // On MOBILE (viewport < 640px) we ignore the requested `placement` and
  // instead dock the tooltip at the opposite edge of the screen from the
  // target — a proven mobile-tour pattern that guarantees the spotlight
  // stays visible and the tooltip never overlaps the anchor. On DESKTOP
  // the existing bottom / left / bottom-end placements work fine.
  const tooltipStyle = (() => {
    if (isCentered) {
      return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const isMobile = vw < 640;

    if (isMobile) {
      // Mobile: place tooltip DIRECTLY above or below the target — whichever
      // side has more room — with maxHeight capped to that free space so
      // the tooltip can never physically overlap the spotlight. This is
      // the "guaranteed no-overlap" formula: the tooltip lives in the
      // strip of screen not occupied by the target.
      const tooltipW = vw - 24;
      const left = 12;
      const margin = 12;                    // gap between tooltip and target
      const edgePad = 12;                   // gap between tooltip and screen edge
      const spaceBelow = vh - (rect.top + rect.height) - margin - edgePad;
      const spaceAbove = rect.top - margin - edgePad;
      const dockBelow = spaceBelow >= spaceAbove;
      const availH = Math.max(140, dockBelow ? spaceBelow : spaceAbove);
      const style = {
        left: `${left}px`,
        width: `${tooltipW}px`,
        maxHeight: `${Math.floor(availH)}px`,
        overflowY: 'auto',
      };
      if (dockBelow) {
        // Sit right below the target — target on top, tooltip below.
        style.top = `${Math.floor(rect.top + rect.height + margin)}px`;
      } else {
        // Sit right above the target — target below, tooltip above.
        style.bottom = `${Math.floor(vh - rect.top + margin)}px`;
      }
      return style;
    }

    // Desktop: original placement-aware logic.
    const tooltipW = Math.min(340, vw - 24);
    const placement = step.placement || 'bottom';
    let top = rect.top + rect.height + 12;
    let left = rect.left + rect.width / 2 - tooltipW / 2;
    if (placement === 'left') {
      top = rect.top + rect.height / 2 - 60;
      left = rect.left - tooltipW - 12;
    } else if (placement === 'bottom-end') {
      top = rect.top + rect.height + 12;
      left = rect.left + rect.width - tooltipW;
    }
    // Clamp to viewport
    left = Math.max(12, Math.min(left, vw - tooltipW - 12));
    top = Math.max(12, top);
    return { top: `${top}px`, left: `${left}px`, width: `${tooltipW}px` };
  })();

  return (
    <div className="tour-root" data-testid="tour-root" role="dialog" aria-modal="true">
      {/* Full-viewport dark backdrop with a cut-out around the target */}
      {isCentered ? (
        <div className="tour-backdrop tour-backdrop-solid" onClick={next} />
      ) : (
        <>
          {/* Four backdrop panels around the spotlight so the target stays clickable-through visually */}
          <div className="tour-backdrop-panel" style={{ top: 0, left: 0, right: 0, height: rect.top - PADDING }} />
          <div className="tour-backdrop-panel" style={{ top: rect.top - PADDING, left: 0, width: rect.left - PADDING, height: rect.height + PADDING * 2 }} />
          <div className="tour-backdrop-panel" style={{ top: rect.top - PADDING, left: rect.left + rect.width + PADDING, right: 0, height: rect.height + PADDING * 2 }} />
          <div className="tour-backdrop-panel" style={{ top: rect.top + rect.height + PADDING, left: 0, right: 0, bottom: 0 }} />
          {/* Animated ring around the target */}
          <div
            className="tour-spotlight"
            style={{
              top: rect.top - PADDING,
              left: rect.left - PADDING,
              width: rect.width + PADDING * 2,
              height: rect.height + PADDING * 2,
            }}
          />
        </>
      )}

      <div className="tour-tooltip" style={tooltipStyle} data-testid={`tour-step-${step.id}`}>
        <div className="tour-step-count">{t('tours.onboarding.stepCount', { current: idx + 1, total: STEPS.length })}</div>
        <div className="tour-title">{t(`tours.onboarding.${step.id}Title`)}</div>
        <div className="tour-body">
          {step.richBody ? (
            <>
              <p style={{ margin: '0 0 10px' }}>{t('tours.onboarding.fairByDesignIntro')}</p>
              <ul className="tour-fair-list">
                <li><Trans i18nKey="tours.onboarding.fairByDesignBullet1" components={{ 1: <b /> }} /></li>
                <li><Trans i18nKey="tours.onboarding.fairByDesignBullet2" components={{ 1: <b />, 2: <em /> }} /></li>
                <li><Trans i18nKey="tours.onboarding.fairByDesignBullet3" components={{ 1: <b /> }} /></li>
              </ul>
              <p style={{ margin: '10px 0 0', fontSize: '12px', color: 'var(--text-tertiary)' }}>{t('tours.onboarding.fairByDesignFoot')}</p>
            </>
          ) : (
            t(`tours.onboarding.${step.id}Body`)
          )}
        </div>
        <div className="tour-actions">
          <button className="tour-btn-ghost" onClick={finish} data-testid="tour-skip-btn">
            {t('tours.common.skipTour')}
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            {idx > 0 && (
              <button className="tour-btn-ghost" onClick={back} data-testid="tour-back-btn">
                {t('tours.common.back')}
              </button>
            )}
            <button className="tour-btn-primary" onClick={next} data-testid="tour-next-btn">
              {idx >= STEPS.length - 1 ? t('tours.common.done') : t('tours.common.next')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
